import { Component, OnInit, Input, OnChanges, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { UserService } from '@Services/user/user.service';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-university-picker',
    templateUrl: './university-picker.component.html',
    styleUrls: ['./university-picker.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, MatTooltipModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UniversityPickerComponent implements OnChanges {
    @Input() profileForm;
    @Input() profileData: any;

    // custome country alert interface options
    customCountryAlertOptions: any = {
        header: 'Which country did you attend University?',
        cssClass: 'time-selection',
        translucent: true,
    };

    // custome school alert interface options
    customSchoolAlertOptions: any = {
        header: 'Select Your University',
        cssClass: 'time-selection',
        translucent: true,
    };

    // used for testing and design
    countries: any[] = [];

    selectedCountry: string;
    schoolSelection: string;

    schools: any;
    disableSchoolSelection: boolean = true;
    disableCountrySelection: boolean = false;

    stopwatchStart;

    constructor(private userService: UserService, private router: Router, private angulartics2GoogleTagManager: Angulartics2GoogleTagManager) {}

    /**
     * sendEventActions
     * @params {object} object
     */
    sendEventActions(object) {
        // send Analytics (Angulartics) pageview
        if (object.pageview) {
            setTimeout(() => {
                this.angulartics2GoogleTagManager.pageTrack(object.pageview);
            }, 1);
        }

        // send Analytics (Angulartics) event
        // only action is required, but send category, label, and value if available
        if (object.eventAction) {
            setTimeout(() => {
                this.angulartics2GoogleTagManager.eventTrack(object.eventAction, {
                    category: object.eventCategory,
                    label: object.eventLabel,
                    value: object.eventValue,
                });
            }, 1);
        }

        // send DataDog RUM action
        if (object.ddAction) {
            object.ddContext = object.ddContext || {};
            object.ddContext.postInitTime = performance.now() - this.stopwatchStart;

            setTimeout(() => {
                datadogRum.addAction(object.ddAction, object.ddContext);
            }, 1);
        }
    }

    /**
     * ngOnChanges
     */
    ngOnChanges() {
        this.stopwatchStart = this.stopwatchStart || performance.now();

        this.schoolSelection = this.profileData.university;
        if (this.profileData.status === 'Pre-Med') {
            this.schoolSelection = 'Undergraduate School';
            // this.selectedCountry = 'United States of America';
            this.disableSchoolSelection = true;
            this.disableCountrySelection = true;
            // this.profileForm.patchValue({
            //   status: 'Pre-Med',
            //   university: 'Undergraduate School'
            // });
        } else {
            this.userService.getUniversityCountryList().subscribe((response: any) => {
                this.countries = response.countries;
            });
        }
    }

    // object1, object2 - used for comparing objects for better template rendering
    // used for 2d array tracking using [compareWith]=compareWithFn on ion-select
    compareWithFn = (o1: any, o2: any) => {
        return o1 && o2 ? o1.id === o2.id : o1 === o2;
    };

    // used for 1d array tracking
    trackByIndex = (index: number): number => index;

    countryChange(e: any) {
        if (this.profileData.status !== 'Pre-Med') {
            if (!this.selectedCountry && e.detail.value) {
                this.disableSchoolSelection = true;

                // event tracking
                this.sendEventActions({
                    pageview: '/universityPickerCountrySelection',
                    eventAction: 'universityPickerCountrySelection',
                    eventCategory: 'University Picker',
                    ddAction: 'user_universityPicker_countrySelection',
                    ddContext: {
                        userStatus: this.profileData ? this.profileData.status : null,
                        countryName: e.detail.value,
                    },
                });
                // this.angulartics2GoogleTagManager.pageTrack('/universityPickerCountrySelection');
                // this.angulartics2GoogleTagManager.eventTrack('universityPickerCountrySelection', { category: 'University Picker' });

                const countryname = e.detail.value;
                this.userService.getUniversityList(countryname).subscribe((response: any) => {
                    if (!this.profileForm.get('university').value) {
                        this.profileForm.patchValue({
                            university: null,
                        });
                    }
                    this.schools = response.universities;
                    this.disableSchoolSelection = false;

                    // event tracking
                    this.sendEventActions({
                        eventAction: 'universityPickerSchoolsLoaded',
                        eventCategory: 'University Picker',
                        ddAction: 'user_universityPicker_schoolsLoaded',
                        ddContext: {
                            countryName: e.detail.value,
                        },
                    });
                });
            }
        } else {
            this.selectedCountry = 'United States of America';
        }
    }

    schoolChange(e: any) {
        // event tracking
        this.sendEventActions({
            pageview: '/06-ProfileUniversityValid',
            eventAction: '06-ProfileUniversityValid',
            eventCategory: 'Profile Block Academic',
            ddAction: 'user_universityPicker_06-ProfileUniversityValid',
            ddContext: {
                schoolName: e.detail.value,
            },
        });
        // this.angulartics2GoogleTagManager.pageTrack('/06-ProfileUniversityValid');
        // this.angulartics2GoogleTagManager.eventTrack('06-ProfileUniversityValid', { category: 'Profile Block' });

        this.profileForm.patchValue({
            university: e.detail.value,
        });
    }

    academicStatusChange(e: any) {
        // this.profileForm.patchValue({
        //   university: this.profileForm.get('status').value
        // });

        if (this.router.url === '/my-profile-block' && e.detail.value !== this.profileData.status) {
            // event tracking
            this.sendEventActions({
                pageview: '07-ProfileAcademicStatusValid',
                eventAction: '07-ProfileAcademicStatusValid',
                eventCategory: 'Profile Block Academic',
                ddAction: 'user_universityPicker_07-ProfileAcademicStatusValid',
                ddContext: {
                    schoolName: e.detail.value,
                },
            });
            // this.angulartics2GoogleTagManager.eventTrack(
            //     '07-ProfileAcademicStatusValid',
            //     { category: 'Profile Block Academic' }
            // );
        }

        if (e.detail.value === 'Pre-Med') {
            // this.countries = ['United States of America'];
            this.profileData.status = e.detail.value;
            // this.schools = [{ schoolid: '000009', schoolname: 'Undergraduate School' }];

            this.selectedCountry = 'United States of America';

            this.schoolSelection = 'Undergraduate School';
            this.profileForm.patchValue({
                status: e.detail.value,
                university: 'Undergraduate School',
            });

            this.disableSchoolSelection = true;
            this.disableCountrySelection = true;
        } else {
            if (this.profileData.status === 'Pre-Med') {
                this.schoolSelection = null;
            }
            this.profileData.status = e.detail.value;
            this.disableSchoolSelection = true;
            this.disableCountrySelection = false;
            this.selectedCountry = null;
            this.profileData.university = null;
            this.profileForm.patchValue({
                status: e.detail.value,
            });
        }
    }
}
